<template>
  <span>
    <widget-common-props :field="field" @changeProp="changeProp" />

    <b-form-group label-cols="3" label="Placeholder">
      <b-form-input
        @change="changeProp"
        label="Placeholder"
        v-model="field.params.placeholder"
      />
    </b-form-group>
    <b-form-group label-cols="3" label="Tooltip">
      <b-form-input
        @change="changeProp"
        label="Tooltip"
        v-model="field.params.tooltip"
      />
    </b-form-group>

    <b-form-group label-cols="3" label="Layout">
      <b-form-select @change="changeProp" v-model="field.params.layout">
        <option value="select">Drop Down</option>
        <option value="buttons">Buttons</option>
        <option value="radios">Radio Buttons</option>
      </b-form-select>
    </b-form-group>

    <b-form-group
      label-cols="3"
      label=""
      v-if="field.params.layout == 'buttons' || field.params.layout == 'radios'"
    >
      <b-form-checkbox @input="changeProp" v-model="field.params.stacked"
        >Stacked</b-form-checkbox
      >
    </b-form-group>

    <!-- Data Source Toggle -->
    <b-form-group label-cols="3" label="Data Source">
      <b-form-radio-group
        v-model="field.params.data_source"
        @change="onDataSourceChange"
        :options="[
          { value: 'static', text: 'Static Options' },
          { value: 'ajax', text: 'AJAX URL' },
        ]"
        buttons
        button-variant="outline-secondary"
        size="sm"
      />
    </b-form-group>

    <!-- AJAX Configuration -->
    <template v-if="isAjaxMode">
      <b-form-group label-cols="3" label="AJAX URL">
        <b-form-input
          @change="changeProp"
          placeholder="/api/my-app/select-options/"
          v-model="field.params.ajax_url"
        />
        <small class="text-muted">
          Endpoint must return <code>[{"value": 1, "text": "Label"}, ...]</code>
        </small>
      </b-form-group>

      <b-form-group label-cols="3" label="">
        <b-form-checkbox @input="changeProp" v-model="field.params.send_auth"
          >Send Authorization Header</b-form-checkbox
        >
        <small class="text-muted">Uncheck for public/third-party endpoints</small>
      </b-form-group>

      <b-form-group label-cols="3" label="">
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="!field.params.ajax_url || ajaxLoading"
          @click="testAjax"
        >
          <span v-if="ajaxLoading">Testing...</span>
          <span v-else>Test URL</span>
        </b-button>

        <div v-if="ajaxError" class="mt-1 text-danger small">
          Error: {{ ajaxError }}
        </div>
        <div v-else-if="testResults" class="mt-1 text-success small">
          {{ testResults }}
        </div>
      </b-form-group>
    </template>

    <!-- Static Options -->
    <template v-if="!isAjaxMode">
      <b-card no-body>
        <b-tabs card>
          <b-tab title="Edit Table" active>
            <table>
              <thead>
                <th>Index</th>
                <th>
                  <b-form-select
                    :options="CODE_TYPE_LIST"
                    @change="changeProp"
                    v-model="field.params.valueType"
                  />
                </th>
                <th>Description</th>
                <th><b-button @click="addOption">Add</b-button></th>
              </thead>

              <tr v-for="(item, item_idx) in field.params.options" :key="item_idx">
                <td>{{ item_idx + 1 }}</td>
                <td>
                  <b-form-input
                    :type="field.params.valueType"
                    @change="changeProp"
                    v-model="field.params.options[item_idx].value"
                  ></b-form-input>
                </td>
                <td>
                  <b-form-input
                    @change="changeProp"
                    v-model="field.params.options[item_idx].text"
                  ></b-form-input>
                </td>
                <td>
                  <o-list-actions
                    :list="field.params.options"
                    :index="item_idx"
                  ></o-list-actions>
                </td>
              </tr>
            </table>
          </b-tab>
          <b-tab title="Text Lines">
            <b-form-textarea
              :value="optionsAsLines"
              @change="updateLines"
              rows="6"
            >
            </b-form-textarea>
          </b-tab>
        </b-tabs>
      </b-card>

      <!-- Default Settings -->
      <b-form-group label-cols="3" label="">
        <b-form-checkbox @input="changeProp" v-model="field.default_option"
          >Has Default</b-form-checkbox
        >
      </b-form-group>

      <b-form-group
        label-cols="3"
        label="Default Value"
        v-if="field.default_option"
      >
        <b-form-select
          :options="field.params.options"
          :value="field.default_value"
          @change="changeProp"
          @input="setDefault"
        >
        </b-form-select>
      </b-form-group>
    </template>
  </span>
</template>

<script>
import WidgetCommonProps from "../WidgetCommonProps.vue";
import OListActions from "../../../vue-ocom/components/table/OListActions.vue";
import WidgetPropMixin from "../WidgetPropMixin.js";
import AjaxOptionsMixin from "../AjaxOptionsMixin.js";

export default {
  name: "SelectProps",
  components: { OListActions, WidgetCommonProps },
  mixins: [WidgetPropMixin, AjaxOptionsMixin],
  data: function () {
    return {
      CODE_TYPE_LIST: [
        { value: "text", text: "Code" },
        { value: "number", text: "ID" },
      ],
      testResults: null,
    };
  },
  computed: {
    // Override mixin's isAjaxMode — Props uses explicit data_source toggle
    // rather than URL presence, so the designer can switch modes cleanly.
    isAjaxMode() {
      return !!(this.field && this.field.params && this.field.params.data_source === 'ajax');
    },
    optionsAsLines() {
      if (!("options" in this.field.params)) {
        return "";
      }
      return _.map(this.field.params.options, function (line) {
        return line.value + "=" + line.text;
      }).join("\n");
    },
  },
  methods: {
    onDataSourceChange() {
      if (!this.isAjaxMode) {
        this.$set(this.field.params, 'ajax_url', '');
      }
      this.testResults = null;
      this.ajaxError = null;
      this.changeProp();
    },
    async testAjax() {
      this.testResults = null;
      await this.fetchAjaxOptions(this.field.params.ajax_url, this.field.params.send_auth);
      if (!this.ajaxError && this.ajaxOptions.length > 0) {
        const preview = this.ajaxOptions
          .slice(0, 3)
          .map((o) => `"${o.text}"`)
          .join(', ');
        const more = this.ajaxOptions.length > 3
          ? ` (+${this.ajaxOptions.length - 3} more)`
          : '';
        this.testResults = `${this.ajaxOptions.length} result(s): ${preview}${more}`;
      } else if (!this.ajaxError) {
        this.testResults = 'No results returned.';
      }
    },
    addOption: function () {
      if (!("options" in this.field.params)) {
        this.$set(this.field.params, "options", []);
      }

      var options = this.field.params.options;

      var value = null;

      if (this.field.params.valueType != "text") {
        value = (_.maxBy(options, "value") || { value: 0 }).value + 1;
      }

      options.push({ value: value, text: "" });

      console.log(`[${new Date().toISOString()}] SelectProps @change event:`, {
        component: "SelectProps",
        method: "addOption",
        fieldCode: this.field.code,
        optionsCount: options.length,
        field: this.field,
      });
      this.$emit("change", this.field);
    },
    updateLines: function (text) {
      var lines = text.split("\n");

      var options = this.field.params.options || [];

      _.each(lines, function (line, index) {
        var value = null;
        var text = line;

        if (line.includes("=")) {
          var row = line.split("=");

          value = row[0];
          text = row[1];
          try {
            value = _.toNumber(value); // try to convert to a number
          } catch (e) {
            // Ignore
          }
        }
        if (index > options.length - 1) {
          if (value == null) {
            value = (_.maxBy(options, "value") || { value: 0 }).value + 1;
          }

          options.push({ value: value, text: text });
        } else {
          options[index].value = value;
          options[index].text = text;
        }
      });

      this.$set(this.field.params, "options", options);
      this.changeProp();
    },
    setDefault(value) {
      this.$set(this.field, "default_value", value);
    },
  },
};
</script>
